import React, { useEffect, useState } from "react";

import { RefSelect, buildMockGameStatus } from "./BuilderUtils";
import { authoredDecisionIds } from "./measurePool";
import PageCanvas from "./PageCanvas";
import { displayInfoOf } from "./pageKinds";

/**
 * CARTE DES CONSÉQUENCES — qui ajoute quoi au parcours, après cette page.
 *
 * LA FORME. Un tableau : une ligne par mesure, deux colonnes — ce qui arrive si
 * elle est prise, ce qui arrive si elle ne l'est pas. À l'intersection, une
 * étiquette par règle. Ces règles étaient auparavant rangées par document (les
 * unes dans la page, les autres dans chacune des mesures, chacune derrière son
 * écran), alors que la question qu'on se pose n'est jamais « que fait cette
 * décision ? » mais « qu'est-ce qui peut arriver après cette page ? ». Une
 * question d'ensemble appelle une vue d'ensemble.
 *
 * CE QUI EST MONTRÉ EST CE QUI SERA JOUÉ, DANS CET ATELIER-LÀ. Une règle peut
 * porter des variantes par atelier (`variation_game_config`) : le moteur, s'il
 * en trouve une pour l'atelier en cours, l'applique À LA PLACE de la règle par
 * défaut — et si elle vaut `null`, il n'applique rien du tout. L'écran fait donc
 * la même résolution et affiche le résultat, avec une étiquette qui dit d'où il
 * vient. Montrer la règle par défaut alors qu'un autre parcours sera joué serait
 * exactement le genre de surprise qu'on découvre en séance.
 *
 * CE QUE LE MOTEUR SAIT FAIRE N'EST PAS SYMÉTRIQUE, et l'écran ne le cache pas :
 * une conséquence de MESURE ne peut être conditionnée qu'à une décision passée
 * (cf. game_functions.py), là où une conséquence de PAGE accepte aussi les
 * seuils de score. Proposer un seuil sur une mesure laisserait écrire une règle
 * qui ne se déclencherait jamais.
 */

const BRANCH = {
  taken: { field: "add_elem_to_pile_if_taken", label: "Si la mesure est prise" },
  not_taken: { field: "add_elem_to_pile_if_not_taken", label: "Si elle n'est pas prise" },
};

/**
 * CE QUE CET ATELIER JOUERA, pour une règle donnée. Reproduit exactement la
 * lecture du moteur : une clé présente pour cet atelier l'emporte sur la règle
 * par défaut, y compris quand elle vaut `null` (« ici, rien »).
 */
export function resolveConsequence(consequence, configId) {
  const variations = consequence.variation_game_config || {};
  if (configId && Object.prototype.hasOwnProperty.call(variations, configId)) {
    return { targetId: variations[configId] || null, scope: "workshop" };
  }
  return { targetId: consequence.consequence_id || null, scope: "default" };
}

/** Les autres ateliers qui dévient de la règle par défaut. */
function otherVariations(consequence, configId) {
  return Object.keys(consequence.variation_game_config || {}).filter((id) => id !== configId);
}

export default function ConsequenceMap({
  page, ctx, decisionsById, configId, configName,
  onPatch, onChangeDecision, onEditDecision,
  // La page cible s'édite d'ici (par-dessus, cf. PageComposer) : onEditPage(id)
  // l'ouvre, onCreatePage(onCreated) en ouvre une neuve et rappelle onCreated
  // avec son identifiant une fois écrite.
  onEditPage, onCreatePage,
  fullscreen, onToggleFullscreen,
}) {
  const { allPages = [] } = ctx;
  // UN EMPLACEMENT SE DÉSIGNE, IL NE SE CAPTURE PAS. La première version
  // gardait l'objet règle dans l'état au moment de l'ouverture : la fenêtre de
  // réglages montrait donc un instantané, et ses propres modifications ne s'y
  // reflétaient pas — changer la portée n'avait l'air de rien faire. On garde
  // désormais l'ADRESSE de la règle, et on la relit à chaque rendu.
  const [settingsRef, setSettingsRef] = useState(null);
  const [removingRef, setRemovingRef] = useState(null);
  const [peekPageId, setPeekPageId] = useState(null);
  const [showLegend, setShowLegend] = useState(false);

  const decisions = authoredDecisionIds(page, ctx.config).map((id) => decisionsById[id]).filter(Boolean);
  const pageTitle = (id) => allPages.find((p) => p._id === id)?.title || id;
  const pageExists = (id) => allPages.some((p) => p._id === id);
  // La page ouverte ne s'édite pas par-dessus elle-même : le bouton le dit
  // plutôt que de ne rien faire.
  const editableTarget = (id) => !!id && pageExists(id) && id !== page._id;

  // L'aperçu se ferme quand on passe à l'édition : il montrait la page telle
  // qu'elle était à l'ouverture, et resterait dessous, périmé.
  const editPage = (id) => {
    setPeekPageId(null);
    onEditPage?.(id);
  };

  /* ── Un emplacement de règle, quelle que soit sa provenance ──────────
     Les règles de la page et celles des mesures vivent dans des documents
     différents mais se manipulent pareil. Les étiquettes et les réglages ne
     connaissent donc que cette abstraction, jamais l'endroit d'où la règle
     sort. */
  const pageSlot = (consequence, index) => ({
    key: `page|${index}`,
    kind: "page",
    ref: { kind: "page", index },
    consequence,
    index,
    update: (next) => onPatch({
      page_general_consequence: (page.page_general_consequence || []).map((c, i) => (i === index ? next : c)),
    }),
    remove: () => onPatch({
      page_general_consequence: (page.page_general_consequence || []).filter((_, i) => i !== index),
    }),
  });

  const decisionSlot = (decision, branch, consequence, index) => ({
    key: `${decision._id}|${branch}|${index}`,
    kind: "decision",
    ref: { kind: "decision", decisionId: decision._id, branch, index },
    decision,
    branch,
    consequence,
    index,
    update: (next) => onChangeDecision(decision._id, {
      ...decision,
      [BRANCH[branch].field]: (decision[BRANCH[branch].field] || []).map((c, i) => (i === index ? next : c)),
    }),
    remove: () => onChangeDecision(decision._id, {
      ...decision,
      [BRANCH[branch].field]: (decision[BRANCH[branch].field] || []).filter((_, i) => i !== index),
    }),
  });

  /** Relit une règle depuis les documents courants, à partir de son adresse. */
  const slotFromRef = (ref) => {
    if (!ref) return null;
    if (ref.kind === "page") {
      const consequence = (page.page_general_consequence || [])[ref.index];
      return consequence ? pageSlot(consequence, ref.index) : null;
    }
    const decision = decisionsById[ref.decisionId];
    const consequence = decision?.[BRANCH[ref.branch].field]?.[ref.index];
    return consequence ? decisionSlot(decision, ref.branch, consequence, ref.index) : null;
  };

  // Une règle qu'on vient d'ajouter est vide : elle s'ouvre sur ses réglages,
  // parce qu'une étiquette « Rien » ne dit rien de ce qu'on voulait faire.
  const addPageRule = () => {
    const list = [...(page.page_general_consequence || []), { condition: false, consequence_id: "" }];
    onPatch({ page_general_consequence: list });
    setSettingsRef({ kind: "page", index: list.length - 1, isNew: true });
  };

  // AJOUTER À UNE MESURE DEPUIS CET ÉCRAN, C'EST AJOUTER POUR CET ATELIER. La
  // mesure est partagée : une règle créée sans variante s'appliquerait d'un coup
  // à tous les ateliers qui la proposent, ce qui n'est jamais ce qu'on veut en
  // composant un atelier précis. La règle naît donc vide par défaut, avec une
  // variante pour l'atelier en cours — les autres parcours ne bougent pas.
  const addDecisionRule = (decision, branch) => {
    const field = BRANCH[branch].field;
    const fresh = configId
      ? { condition: false, consequence_id: null, variation_game_config: { [configId]: null } }
      : { condition: false, consequence_id: "" };
    const list = [...(decision[field] || []), fresh];
    onChangeDecision(decision._id, { ...decision, [field]: list });
    setSettingsRef({ kind: "decision", decisionId: decision._id, branch, index: list.length - 1, isNew: true });
  };

  /* ── Contrôle : ce qui casserait une séance ─────────────────────── */
  const allSlots = [
    ...(page.page_general_consequence || []).map(pageSlot),
    ...decisions.flatMap((d) => Object.keys(BRANCH).flatMap((b) =>
      (d[BRANCH[b].field] || []).map((c, i) => decisionSlot(d, b, c, i))
    )),
  ];
  const broken = [...new Set(
    allSlots
      .map((slot) => resolveConsequence(slot.consequence, configId).targetId)
      .filter((id) => id && !pageExists(id))
  )];

  const chip = (slot) => (
    <ConsequenceChip
      key={slot.key}
      slot={slot}
      configId={configId}
      pageTitle={pageTitle}
      pageExists={pageExists}
      editableTarget={editableTarget}
      onPeek={setPeekPageId}
      onEdit={editPage}
      onSettings={() => setSettingsRef(slot.ref)}
      onRemove={() => setRemovingRef(slot.ref)}
    />
  );

  return (
    <div className="consequence-map">
      <div className="score-matrix-toolbar">
        <div className="consequence-map-context">
          {configName ? (
            <>Parcours tel qu'il sera joué dans <strong>{configName}</strong>.</>
          ) : (
            <>Aucun atelier en contexte : les variantes par atelier ne peuvent pas être résolues.</>
          )}
          {" "}Les règles portées par une mesure lui appartiennent — les modifier
          ici les modifie partout où elle est proposée. Rien n'est écrit avant « Enregistrer ».
        </div>
        <button
          type="button"
          className="builder-btn-secondary"
          onClick={() => setShowLegend((v) => !v)}
          title="Que veulent dire les étiquettes ?"
        >{showLegend ? "Masquer la légende" : "? Légende"}</button>
        <button
          type="button"
          className="builder-btn-secondary score-matrix-expand"
          onClick={onToggleFullscreen}
          title={fullscreen ? "Réduire (Échap)" : "Agrandir le tableau à tout l'écran"}
        >{fullscreen ? "⤡ Réduire" : "⤢ Plein écran"}</button>
      </div>

      {showLegend && <ConsequenceLegend configName={configName} />}

      {broken.length > 0 && (
        <div className="builder-warning-inline">
          ⚠ Page(s) cible(s) introuvable(s) en base : {broken.join(", ")}. En séance,
          le parcours s'arrêtera là.
        </div>
      )}

      <div className="consequence-table-scroll">
        <table className="consequence-table">
          <thead>
            <tr>
              <th className="consequence-table-rowhead">Mesure</th>
              <th>{BRANCH.taken.label}</th>
              <th>{BRANCH.not_taken.label}</th>
            </tr>
          </thead>
          <tbody>
            {/* LA PAGE ELLE-MÊME. Ses règles ne dépendent d'aucun choix : elles
                occupent donc les deux colonnes plutôt que d'en choisir une, ce
                qui reviendrait à dire quelque chose de faux. */}
            <tr className="consequence-table-pagerow">
              <th className="consequence-table-rowhead">
                <span className="consequence-map-icon">{displayInfoOf(page)?.icon || "📄"}</span>
                <span className="consequence-table-rowtitle">Cette page, une fois validée</span>
              </th>
              <td colSpan={2}>
                <div className="consequence-cell">
                  {(page.page_general_consequence || []).map((c, i) => chip(pageSlot(c, i)))}
                  <AddRule empty={(page.page_general_consequence || []).length === 0} onClick={addPageRule} />
                </div>
              </td>
            </tr>

            {decisions.map((decision, idx) => (
              <tr key={decision._id}>
                <th className="consequence-table-rowhead">
                  <span className="consequence-map-index">n°{idx + 1}</span>
                  <button
                    type="button"
                    className="builder-linkbtn consequence-table-rowtitle"
                    onClick={() => onEditDecision?.(decision._id)}
                    title="Ouvrir cette mesure"
                  >{decision.title || decision._id}</button>
                </th>
                {Object.keys(BRANCH).map((branch) => (
                  <td key={branch}>
                    <div className="consequence-cell">
                      {(decision[BRANCH[branch].field] || []).map((c, i) =>
                        chip(decisionSlot(decision, branch, c, i))
                      )}
                      <AddRule
                        empty={(decision[BRANCH[branch].field] || []).length === 0}
                        onClick={() => addDecisionRule(decision, branch)}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}

            {decisions.length === 0 && (
              <tr>
                <td colSpan={3} className="builder-empty">
                  Cette page n'a pas de mesures : seules ses propres conséquences
                  peuvent ajouter quelque chose au parcours.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {slotFromRef(settingsRef) && (
        <ConsequenceSettings
          key={`${settingsRef.kind}-${settingsRef.decisionId || "page"}-${settingsRef.branch || ""}-${settingsRef.index}`}
          slot={slotFromRef(settingsRef)}
          ctx={ctx}
          configId={configId}
          configName={configName}
          pageTitle={pageTitle}
          onPeek={setPeekPageId}
          onCreatePage={onCreatePage}
          onOpenDecision={() => {
            const slot = slotFromRef(settingsRef);
            setSettingsRef(null);
            if (settingsRef.isNew) slot.remove();
            onEditDecision?.(settingsRef.decisionId);
          }}
          onCancel={() => {
            // Une règle créée à l'instant puis abandonnée ne doit pas rester :
            // on ouvre souvent des réglages pour voir, et se raviser.
            if (settingsRef.isNew) slotFromRef(settingsRef)?.remove();
            setSettingsRef(null);
          }}
          onConfirm={(next) => {
            slotFromRef(settingsRef)?.update(next);
            setSettingsRef(null);
          }}
        />
      )}

      {slotFromRef(removingRef) && (
        <RemoveMenu
          slot={slotFromRef(removingRef)}
          configId={configId}
          configName={configName}
          pageTitle={pageTitle}
          onClose={() => setRemovingRef(null)}
        />
      )}

      {peekPageId && (
        <PagePeek
          pageId={peekPageId}
          ctx={ctx}
          onEdit={editableTarget(peekPageId) ? () => editPage(peekPageId) : null}
          onClose={() => setPeekPageId(null)}
        />
      )}
    </div>
  );
}

/**
 * AJOUTER UNE RÈGLE, SANS COÛTER UNE LIGNE À CHAQUE FOIS.
 *
 * Dans une case vide, le bouton EST le contenu : il occupe la case, se voit, et
 * dit ce qu'on peut y faire. Dès qu'une règle y figure, il n'est plus qu'un
 * complément — une pastille discrète au bout de la liste, qui ne double plus la
 * hauteur de chaque ligne du tableau.
 */
function AddRule({ empty, onClick }) {
  return (
    <button
      type="button"
      className={`consequence-add${empty ? " is-empty" : " is-compact"}`}
      onClick={onClick}
      title="Ajouter une conséquence dans cette case"
    >{empty ? "+ Ajouter une conséquence" : "+"}</button>
  );
}

/* ─── L'étiquette ─────────────────────────────────────────────────────
   Ce que cet atelier jouera, plus d'où ça vient, plus quatre gestes : voir la
   page cible, la modifier, régler la règle, la retirer. Le nom de la page cible
   passe avant tout le reste — c'est ce qu'on cherche en balayant le tableau.
*/
function ConsequenceChip({ slot, configId, pageTitle, pageExists, editableTarget, onPeek, onEdit, onSettings, onRemove }) {
  const { consequence } = slot;
  const { targetId, scope } = resolveConsequence(consequence, configId);
  const others = otherVariations(consequence, configId);
  const missing = targetId && !pageExists(targetId);
  const editTitle = !targetId
    ? "Aucune page à modifier"
    : missing
      ? "Cette page n'existe plus"
      : !editableTarget(targetId)
        ? "C'est la page ouverte"
        : "Modifier cette page";

  return (
    <div className={`consequence-chip is-${scope}${targetId ? "" : " is-void"}`}>
      <span className="consequence-chip-name" title={targetId || "Aucune page ajoutée"}>
        {targetId ? pageTitle(targetId) : "Rien"}
      </span>

      <span className="consequence-chip-tags">
        {scope === "workshop" && (
          <span className="consequence-tag is-workshop" title="Une variante propre à cet atelier remplace ici la règle par défaut">
            propre à l'atelier
          </span>
        )}
        {scope === "default" && (
          <span className="consequence-tag is-default" title="Conséquence de la mesure, commune à tous les ateliers">
            par défaut
          </span>
        )}
        {scope === "default" && others.length > 0 && (
          <span className="consequence-tag is-other" title={`Cette règle est remplacée dans : ${others.join(", ")}`}>
            {others.length} variante{others.length > 1 ? "s" : ""} ailleurs
          </span>
        )}
        {consequence.condition && (
          <span className="consequence-tag is-cond" title="Cette règle ne s'applique que si une condition est remplie">
            conditionnelle
          </span>
        )}
        {missing && (
          <span className="consequence-tag is-broken" title="Cette page n'existe plus en base">introuvable</span>
        )}
      </span>

      <span className="consequence-chip-tools">
        <button
          type="button"
          className="consequence-chip-btn"
          onClick={() => onPeek(targetId)}
          disabled={!targetId || missing}
          title={targetId ? "Voir cette page" : "Aucune page à voir"}
        >👁</button>
        <button
          type="button"
          className="consequence-chip-btn"
          onClick={() => onEdit(targetId)}
          disabled={!editableTarget(targetId)}
          title={editTitle}
        >✏</button>
        <button type="button" className="consequence-chip-btn" onClick={onSettings} title="Réglages de cette conséquence">⚙</button>
        <button type="button" className="consequence-chip-btn is-danger" onClick={onRemove} title="Retirer cette conséquence">🗑</button>
      </span>
    </div>
  );
}

/* ─── Les réglages d'une règle ────────────────────────────────────────
   CE QUE LE MOTEUR PERMET DE PERSONNALISER, ET RIEN DE PLUS.
   `variation_game_config` ne porte QUE la page cible : la condition est
   évaluée avant, sur l'objet partagé (cf. game_functions.py). Une condition
   « propre à un atelier » n'existe donc pas — la changer ici la changerait
   partout, sans que rien à l'écran ne le laisse deviner.

   D'où la règle de cet écran : on y compose UN ATELIER, pas la bibliothèque
   partagée.
   · Une conséquence de PAGE est un champ de la page qu'on édite : elle se règle
     entièrement ici.
   · Une conséquence de MESURE appartient à un document partagé : seule sa cible
     pour CET atelier s'y modifie. Le reste — la conséquence par défaut, sa
     condition — se règle dans la mesure, et l'encart y renvoie.

   RIEN N'EST ÉCRIT AVANT « TERMINER ». La croix ferme sans rien garder, et
   retire la règle si elle venait d'être créée : ouvrir des réglages pour voir,
   puis se raviser, ne doit pas laisser une règle vide derrière soi.
*/
function ConsequenceSettings({
  slot, ctx, configId, configName, pageTitle, onPeek, onCreatePage, onOpenDecision, onCancel, onConfirm,
}) {
  const { allPages = [], allDecisions = [], scoreDic } = ctx;
  const { kind, branch, decision } = slot;

  const [draft, setDraft] = useState(slot.consequence);
  const patch = (fields) => setDraft((d) => ({ ...d, ...fields }));

  const { scope, targetId } = resolveConsequence(draft, configId);
  const others = otherVariations(draft, configId);
  const editableTarget = kind === "page" || scope === "workshop";

  const pageOptions = allPages.map((p) => ({ value: p._id, label: p.title || p._id, sublabel: p.type }));
  const decisionOptions = allDecisions.map((d) => ({ value: d._id, label: d.title || d._id }));
  const scoreOptions = Object.entries(scoreDic || {}).map(([key, s]) => ({ value: key, label: s.name, sublabel: s.type }));

  const setTarget = (value) => {
    if (scope === "workshop") {
      return patch({
        variation_game_config: { ...(draft.variation_game_config || {}), [configId]: value || null },
      });
    }
    patch({ consequence_id: value });
  };

  // Personnaliser emporte la cible actuelle : c'est le point de départ évident
  // d'une variante, et la ressaisir juste après n'aurait aucun sens.
  const customise = () => patch({
    variation_game_config: { ...(draft.variation_game_config || {}), [configId]: draft.consequence_id || null },
  });

  const conditionType = draft.condition
    ? (kind === "decision" ? "past_decision" : (draft.condition_metadata?.type || "past_decision"))
    : "always";

  const setConditionType = (value) => {
    if (value === "always") {
      return patch({ condition: false, condition_metadata: undefined, previous_decision_id: undefined });
    }
    patch({ condition: true, condition_metadata: { ...(draft.condition_metadata || {}), type: value } });
  };

  const setMeta = (fields) =>
    patch({ condition_metadata: { ...(draft.condition_metadata || {}), ...fields } });

  const conditionSentence = () => {
    if (!draft.condition) return "Toujours.";
    const id = kind === "decision" ? draft.previous_decision_id : draft.condition_metadata?.previous_decision_id;
    const label = allDecisions.find((d) => d._id === id)?.title || id;
    return label
      ? `Seulement si la décision « ${label} » a déjà été prise.`
      : "Conditionnelle, mais aucune décision n'est désignée — elle ne se déclenchera jamais.";
  };

  const trigger = kind === "page"
    ? "Après cette page, une fois validée"
    : `${BRANCH[branch].label.toLowerCase()} — « ${decision.title || decision._id} »`;

  return (
    <div className="builder-picker-overlay" onClick={onCancel}>
      <div className="rule-dialog" onClick={(e) => e.stopPropagation()}>
        <header className="rule-dialog-head">
          <div>
            <h3>Conséquence</h3>
            {/* Le déclencheur est rappelé : on ouvre cette fenêtre depuis une
                case d'un tableau de cinquante, et on oublie vite laquelle. */}
            <p className="rule-dialog-trigger">{trigger}</p>
          </div>
          <button className="rule-dialog-close" onClick={onCancel} title="Fermer sans enregistrer">✕</button>
        </header>

        <div className="rule-dialog-body">
          <section className="rule-field">
            <span className="rule-field-label">
              Page ajoutée au parcours
              {kind === "decision" && scope === "workshop" && ` — dans ${configName}`}
            </span>
            {editableTarget ? (
              <div className="rule-field-row">
                <RefSelect
                  value={targetId || ""}
                  onChange={setTarget}
                  options={pageOptions}
                  placeholder="Choisir la page ajoutée…"
                />
                <button
                  type="button"
                  className="builder-btn-secondary"
                  onClick={() => onPeek(targetId)}
                  disabled={!targetId}
                  title="Voir cette page"
                >👁</button>
                {/* LA PAGE QU'ON VEUT AJOUTER N'EXISTE PAS ENCORE — le cas
                    courant quand on écrit une suite. Le composeur s'ouvre
                    par-dessus cette fenêtre, qui reste ouverte dessous et
                    reçoit l'identifiant à la première écriture. Fermer sans
                    écrire ne désigne rien. */}
                {onCreatePage && (
                  <button
                    type="button"
                    className="builder-btn-secondary"
                    onClick={() => onCreatePage(setTarget)}
                    title="Créer la page ajoutée, et la désigner ici une fois enregistrée"
                  >+ Nouvelle page</button>
                )}
              </div>
            ) : (
              <div className="rule-readonly">
                <span className="rule-readonly-value">{targetId ? pageTitle(targetId) : "Aucune page — n'ajoute rien"}</span>
                <button
                  type="button"
                  className="builder-btn-secondary"
                  onClick={() => onPeek(targetId)}
                  disabled={!targetId}
                  title="Voir cette page"
                >👁</button>
              </div>
            )}
            {editableTarget && !targetId && (
              <span className="rule-field-hint">
                Sans page, cette conséquence n'ajoutera rien — c'est aussi la façon
                de neutraliser une suite du parcours sans la supprimer.
              </span>
            )}
          </section>

          <section className="rule-field">
            <span className="rule-field-label">Quand</span>
            {kind === "page" ? (
              <>
                <select className="builder-input" value={conditionType} onChange={(e) => setConditionType(e.target.value)}>
                  <option value="always">Toujours</option>
                  <option value="past_decision">Si une décision a déjà été prise</option>
                  <option value="score_below">Si un score est sous une valeur</option>
                  <option value="score_above">Si un score dépasse une valeur</option>
                </select>

                {conditionType === "past_decision" && (
                  <RefSelect
                    value={draft.condition_metadata?.previous_decision_id || ""}
                    onChange={(v) => setMeta({ previous_decision_id: v })}
                    options={decisionOptions}
                    placeholder="Laquelle ?"
                  />
                )}

                {["score_below", "score_above"].includes(conditionType) && (
                  <div className="rule-field-row">
                    <RefSelect
                      value={draft.condition_metadata?.score_id || ""}
                      onChange={(v) => setMeta({ score_id: v })}
                      options={scoreOptions}
                      placeholder="Quel score ?"
                    />
                    <input
                      className="builder-input small"
                      type="number"
                      placeholder="valeur"
                      value={draft.condition_metadata?.value ?? ""}
                      onChange={(e) => setMeta({ value: Number(e.target.value) })}
                    />
                  </div>
                )}
              </>
            ) : (
              /* La condition d'une conséquence de mesure est commune à tous les
                 ateliers — le moteur l'évalue avant de regarder les variantes.
                 La rendre modifiable ici laisserait croire qu'on ne touche que
                 cet atelier. */
              <div className="rule-readonly">
                <span className="rule-readonly-value">{conditionSentence()}</span>
              </div>
            )}
          </section>

          {kind === "decision" && configId && (
            <section className="rule-field">
              <span className="rule-field-label">Portée</span>
              {scope === "workshop" ? (
                <div className="rule-scope-state is-workshop">
                  <span className="rule-scope-title">Personnalisée pour {configName}</span>
                  <span className="rule-scope-detail">
                    {draft.consequence_id
                      ? <>Les autres ateliers gardent la conséquence par défaut : « {pageTitle(draft.consequence_id)} ».</>
                      : "Les autres ateliers n'ajoutent rien à cet endroit."}
                    {others.length > 0 && ` ${others.length} autre(s) atelier(s) ont aussi leur propre version.`}
                  </span>
                </div>
              ) : (
                <div className="rule-scope-state">
                  <span className="rule-scope-title">Conséquence par défaut de la mesure</span>
                  <span className="rule-scope-detail">
                    Commune à tous les ateliers qui proposent cette mesure
                    {others.length > 0 && `, sauf ${others.length} qui ont déjà leur propre version`}.
                  </span>
                  <button type="button" className="builder-btn-primary rule-scope-action" onClick={customise}>
                    Personnaliser pour {configName}
                  </button>
                </div>
              )}
            </section>
          )}

          {kind === "decision" && (
            <aside className="rule-note">
              La conséquence par défaut et sa condition appartiennent à la mesure,
              partagée par tous les ateliers : elles ne se modifient pas depuis cet écran.
              <button type="button" className="builder-linkbtn" onClick={onOpenDecision}>
                Ouvrir « {decision.title || decision._id} » →
              </button>
            </aside>
          )}
        </div>

        <footer className="rule-dialog-foot">
          <button className="builder-btn-secondary" onClick={onCancel}>Annuler</button>
          <button className="builder-btn-primary" onClick={() => onConfirm(draft)}>Terminer</button>
        </footer>
      </div>
    </div>
  );
}

/* ─── Retirer une règle : deux gestes différents ──────────────────────
   Supprimer une règle par défaut la supprime pour TOUS les ateliers — ce qui
   n'est presque jamais ce qu'on veut quand on travaille dans un atelier
   particulier. L'autre geste, neutraliser, pose une variante vide ici et laisse
   les autres parcours intacts. Les deux étaient indistinguables derrière une
   croix ; ils sont maintenant nommés.
*/
function RemoveMenu({ slot, configId, configName, pageTitle, onClose }) {
  const { consequence } = slot;
  const { scope, targetId } = resolveConsequence(consequence, configId);
  const others = otherVariations(consequence, configId);

  const neutralise = () => {
    slot.update({
      ...consequence,
      variation_game_config: { ...(consequence.variation_game_config || {}), [configId]: null },
    });
    onClose();
  };

  const backToDefault = () => {
    const variations = { ...(consequence.variation_game_config || {}) };
    delete variations[configId];
    slot.update({
      ...consequence,
      variation_game_config: Object.keys(variations).length ? variations : undefined,
    });
    onClose();
  };

  return (
    <div className="builder-picker-overlay" onClick={onClose}>
      <div className="builder-picker" onClick={(e) => e.stopPropagation()}>
        <div className="builder-picker-header">
          <h3>Retirer « {targetId ? pageTitle(targetId) : "cette conséquence"} »</h3>
          <button onClick={onClose}>✕</button>
        </div>

        <div className="consequence-settings">
          {scope === "workshop" ? (
            <>
              <p className="step-editor-section-hint">
                Cette conséquence a une version propre à {configName || "cet atelier"}.
              </p>
              {consequence.consequence_id ? (
                <button className="consequence-choice" onClick={backToDefault}>
                  <strong>Revenir à la conséquence par défaut</strong>
                  <span>
                    La version propre à cet atelier est retirée ; il rejouera
                    « {pageTitle(consequence.consequence_id)} », comme les autres.
                  </span>
                </button>
              ) : (
                <p className="step-editor-section-hint">
                  Cette conséquence n'existe que pour cet atelier : il n'y a pas de
                  version par défaut vers laquelle revenir.
                </p>
              )}
              <button className="consequence-choice is-danger" onClick={() => { slot.remove(); onClose(); }}>
                <strong>Supprimer la conséquence entièrement</strong>
                <span>La conséquence par défaut et toutes ses versions disparaissent, pour tous les ateliers.</span>
              </button>
            </>
          ) : (
            <>
              {configId && (
                <button className="consequence-choice" onClick={neutralise}>
                  <strong>Ne rien ajouter dans {configName || "cet atelier"}</strong>
                  <span>
                    La conséquence par défaut est conservée pour les autres ateliers
                    {others.length > 0 ? ` (et ses ${others.length} variante(s) existante(s))` : ""} ;
                    ici, elle n'ajoutera plus rien.
                  </span>
                </button>
              )}
              <button className="consequence-choice is-danger" onClick={() => { slot.remove(); onClose(); }}>
                <strong>Supprimer pour tous les ateliers</strong>
                <span>
                  La conséquence disparaît partout
                  {others.length > 0 ? `, y compris ses ${others.length} variante(s)` : ""}.
                </span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Coup d'œil sur la page conséquence ──────────────────────────────
   Un titre ne suffit pas à reconnaître une page quand on en a cinquante. On
   montre donc la vraie page, rendue comme en séance — c'est le même canevas que
   celui du composeur, à ceci près qu'il n'est pas éditable.
*/
function PagePeek({ pageId, ctx, onEdit, onClose }) {
  const { token, scoreDic } = ctx;
  const [pageData, setPageData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_API_URL}/get_complete_page/${pageId}`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("Page introuvable"))))
      .then((data) => { if (!cancelled) setPageData(data); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [pageId, token]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <div className="builder-picker-overlay" onClick={onClose}>
      <div className="consequence-peek" onClick={(e) => e.stopPropagation()}>
        <div className="builder-picker-header">
          <h3>
            {displayInfoOf(pageData)?.icon} {pageData?.title || pageId}
            <span className="builder-tag tag-type" style={{ marginLeft: 8 }}>{pageData?.type || "…"}</span>
          </h3>
          <div className="consequence-peek-actions">
            {/* On regarde, on voit la coquille, on corrige : le geste suit le
                regard, sans repasser par le tableau. */}
            {onEdit && pageData && (
              <button type="button" className="builder-btn-secondary" onClick={onEdit}>
                ✏ Modifier cette page
              </button>
            )}
            <button onClick={onClose} title="Fermer (Échap)">✕</button>
          </div>
        </div>
        <div className="consequence-peek-body">
          {error && <div className="builder-empty">Erreur : {error}</div>}
          {!error && !pageData && <div className="builder-empty">Chargement…</div>}
          {/* Le module 3D n'est pas monté pour un simple coup d'œil : reconstruire
              une scène de territoire coûte le rechargement de tout son datapack. */}
          {pageData?.type === "territory_intro" && (
            <div className="builder-empty">
              Module territoire 3D — {(pageData.content?.steps || []).length} étape(s).
              Ouvrez-le pour le parcourir.
            </div>
          )}
          {pageData && pageData.type !== "territory_intro" && (
            <PageCanvas
              pageData={pageData}
              gameStatus={buildMockGameStatus(scoreDic)}
              editMode={false}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ConsequenceLegend({ configName }) {
  return (
    <div className="consequence-legend">
      <p>
        <span className="consequence-tag is-default">par défaut</span>
        La conséquence de la mesure, commune à tous les ateliers qui la proposent.
        Elle se modifie dans la mesure, pas ici.
      </p>
      <p>
        <span className="consequence-tag is-workshop">propre à l'atelier</span>
        Une version remplace ici la conséquence par défaut : c'est elle qui sera
        jouée{configName ? ` dans ${configName}` : ""}, et elle se modifie ici.
      </p>
      <p>
        <span className="consequence-tag is-other">n variantes ailleurs</span>
        Cet atelier joue la conséquence par défaut, mais d'autres ateliers ont la leur.
      </p>
      <p>
        <span className="consequence-tag is-cond">conditionnelle</span>
        Ne s'applique que si sa condition est remplie — sinon rien n'est ajouté.
        La condition est commune à tous les ateliers.
      </p>
      <p>
        <span className="consequence-tag is-broken">introuvable</span>
        La page visée n'existe plus en base : le parcours s'arrêtera là en séance.
      </p>
      <p>
        <span className="consequence-chip is-default is-void consequence-legend-sample">
          <span className="consequence-chip-name">Rien</span>
        </span>
        Aucune page n'est ajoutée — conséquence sans cible, ou neutralisée pour cet atelier.
      </p>
    </div>
  );
}
